import { Pencil1Icon } from '@radix-ui/react-icons'
import { Badge, Container, Flex, Heading, IconButton, Section, Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { Accordion as RadixAccordion } from 'radix-ui'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RewardSchedulesAPI } from '@API/data-services/reward-schedules-service'
import { RootState } from '@AppState/redux-store'
import { Accordion } from '@Components/common/accordion'
import {
  AuthScopes,
  RewardTypeSummary,
  RewardScheduleWithGame,
  S2WGame,
} from '@Generated/back-office-client/src'
import { GameDetails } from './game-details'
import { GameEditDrawer } from './game-edit-drawer'
import { PrizeEditDrawer } from './prize-edit-drawer'
import { PrizeScheduleDetails } from './prize-schedule-details'
import { ScheduleDetailsEditDrawer } from './schedule-details-edit-drawer'
import {
  formatScheduleDates,
  groupSchedulesByGame,
  hasStarted,
  PRIZE_SCHEDULES_QUERY_KEY,
  scheduleStatusColor,
  scheduleStatusLabel,
} from './types'

type EditTarget =
  | { kind: 'game'; game: S2WGame }
  | { kind: 'details'; schedule: RewardScheduleWithGame }
  | { kind: 'prize'; schedule: RewardScheduleWithGame; reward: RewardTypeSummary }

export const SpinToWin = () => {
  const { activeCustomerMetadata, currentUser } = useSelector((state: RootState) => state.users)
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'
  const canEdit = !!currentUser?.scopes.includes(AuthScopes.writespin_to_win)
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null)

  const { data, error, isLoading } = useQuery({
    queryKey: PRIZE_SCHEDULES_QUERY_KEY,
    queryFn: () => RewardSchedulesAPI.getCurrent(),
  })

  const gameGroups = useMemo(
    () => groupSchedulesByGame(data?.games ?? [], data?.schedules ?? []),
    [data],
  )

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="3">
          <Heading as="h1" size="5">
            Spin to Win
          </Heading>
          <Text size="2" color="gray">
            Spin to Win games, with their active, scheduled, and not-ready prize schedules.
          </Text>

          {error ? <Text color="red">Could not load Spin to Win games.</Text> : null}
          {isLoading ? <Text size="2">Loading...</Text> : null}
          {data && gameGroups.length === 0 ? (
            <Text size="2" color="gray">
              No Spin to Win games.
            </Text>
          ) : null}

          {gameGroups.map(({ game, schedules }) => (
            <section key={game.id} aria-label={game.name}>
              <Flex direction="column" gap="2" mt="3">
                <Heading as="h2" size="4">
                  {game.name}
                </Heading>
                <GameDetails
                  game={game}
                  onEdit={canEdit ? () => setEditTarget({ kind: 'game', game }) : undefined}
                />
                {schedules.length === 0 ? (
                  <Text size="2" color="gray">
                    No current prize schedules.
                  </Text>
                ) : null}
                <Accordion
                  items={schedules.map((schedule) => ({
                    id: schedule.id,
                    value: schedule.id,
                    name: schedule.name,
                    actionButton: (
                      <Flex gap="3" align="center">
                        <Text size="2" color="gray">
                          {formatScheduleDates(schedule, timezone)}
                        </Text>
                        <Badge color={scheduleStatusColor(schedule.status)}>
                          {scheduleStatusLabel(schedule.status)}
                        </Badge>
                        {canEdit ? (
                          <IconButton
                            size="1"
                            variant="ghost"
                            aria-label={`Edit ${schedule.name} details`}
                            onClick={() => setEditTarget({ kind: 'details', schedule })}>
                            <Pencil1Icon />
                          </IconButton>
                        ) : null}
                      </Flex>
                    ),
                    child: (
                      <RadixAccordion.Content className="AccordionContent">
                        <PrizeScheduleDetails
                          schedule={schedule}
                          canEndEarly={canEdit && hasStarted(schedule, new Date())}
                          onEditPrize={
                            canEdit
                              ? (reward) => setEditTarget({ kind: 'prize', schedule, reward })
                              : undefined
                          }
                        />
                      </RadixAccordion.Content>
                    ),
                  }))}
                />
              </Flex>
            </section>
          ))}
        </Flex>
      </Section>

      {editTarget?.kind === 'game' ? (
        <GameEditDrawer game={editTarget.game} onClose={() => setEditTarget(null)} />
      ) : null}
      {editTarget?.kind === 'details' ? (
        <ScheduleDetailsEditDrawer
          schedule={editTarget.schedule}
          onClose={() => setEditTarget(null)}
        />
      ) : null}
      {editTarget?.kind === 'prize' ? (
        <PrizeEditDrawer
          schedule={editTarget.schedule}
          reward={editTarget.reward}
          onClose={() => setEditTarget(null)}
        />
      ) : null}
    </Container>
  )
}
